/**
 * Mock LIFF Hook
 * 在開發環境中模擬 LIFF SDK 的行為
 * 只在開發環境中啟用，生產環境使用真實 LIFF
 */

import { useState, useEffect, useCallback } from 'react';

interface MockLiffProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
}

interface MockLiffContext {
  type: 'group' | 'room' | 'none' | 'utou' | 'square';
  groupId?: string;
  roomId?: string;
  userId?: string;
}

interface MockLiff {
  isLoggedIn: () => boolean;
  getIDToken: () => Promise<string>;
  getProfile: () => Promise<MockLiffProfile>;
  getContext: () => Promise<MockLiffContext>;
  openWindow: (options: { url: string; external?: boolean }) => void;
}

/**
 * 預設的 Mock 用戶配置
 */
const MOCK_USER_PROFILES: Record<
  string,
  {
    userId: string;
    displayName: string;
    groupId?: string;
  }
> = {
  admin: {
    userId: 'Uadmin1234567890abcdefghijklmnop',
    displayName: '管理員',
    groupId: 'Ca56f94637cec434d27582291d19f510f',
  },
  member: {
    userId: 'Umember1234567890abcdefghijklmn',
    displayName: '一般會員',
    groupId: 'Ca56f94637cec434d27582291d19f510f',
  },
  guest: {
    userId: 'Uguest1234567890abcdefghijklmnop',
    displayName: '訪客',
    groupId: 'Ca56f94637cec434d27582291d19f510f',
  },
};

/**
 * 從 URL 參數取得 Mock 設定
 */
function getMockConfigFromUrl() {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const mockUser = urlParams.get('mock_user');
  const mockTenant = urlParams.get('mock_tenant');
  const mockUserId = urlParams.get('mock_userId');
  const mockTenantId = urlParams.get('mock_tenantId');
  const mockGroupId = urlParams.get('mock_groupId');

  if (!mockUser && !mockUserId) {
    return null;
  }

  // 如果指定了 mock_user，使用預設配置
  let userId: string;
  let displayName: string;
  let groupId: string;

  if (mockUser && MOCK_USER_PROFILES[mockUser]) {
    const profile = MOCK_USER_PROFILES[mockUser];
    userId = mockUserId || profile.userId;
    displayName = profile.displayName;
    groupId =
      mockGroupId || profile.groupId || 'Ca56f94637cec434d27582291d19f510f';
  } else if (mockUserId) {
    // 自訂用戶 ID
    userId = mockUserId;
    displayName = urlParams.get('mock_displayName') || '自訂用戶';
    groupId = mockGroupId || 'Ca56f94637cec434d27582291d19f510f';
  } else {
    return null;
  }

  return {
    userId,
    displayName,
    groupId,
    tenantId: mockTenantId || mockTenant || null,
  };
}

/**
 * 從環境變數或 localStorage 取得 Mock 設定
 * 優先順序：URL 參數 > localStorage > 環境變數
 */
function getMockConfig() {
  // 優先從 URL 參數取得（最高優先級）
  const urlConfig = getMockConfigFromUrl();
  if (urlConfig) {
    console.log('[Mock LIFF] 從 URL 參數取得配置:', urlConfig);
    return {
      userId: urlConfig.userId,
      groupId: urlConfig.groupId,
      loggedIn: true,
      contextType: 'group' as MockLiffContext['type'],
      displayName: urlConfig.displayName,
      tenantId: urlConfig.tenantId,
    };
  }

  // 從環境變數取得預設值
  const mockUserId =
    import.meta.env.VITE_MOCK_LINE_USER_ID ||
    'U1234567890abcdefghijklmnopqrstuv';
  // 預設使用正確格式的 LINE Group ID（C 開頭，33 字元）
  const mockGroupId =
    import.meta.env.VITE_MOCK_LINE_GROUP_ID ||
    'Ca56f94637cec434d27582291d19f510f';
  const mockLoggedIn = import.meta.env.VITE_MOCK_LIFF_LOGGED_IN !== 'false'; // 預設為 true
  const mockContextType = (import.meta.env.VITE_MOCK_LIFF_CONTEXT_TYPE ||
    'group') as MockLiffContext['type'];
  const mockDisplayName =
    import.meta.env.VITE_MOCK_LINE_DISPLAY_NAME || 'Dev Bob';

  // 從 localStorage 取得覆蓋值（允許在運行時動態調整）
  const storageKey = 'mock_liff_config';
  const stored = localStorage.getItem(storageKey);
  let config = {
    userId: mockUserId,
    groupId: mockGroupId,
    loggedIn: mockLoggedIn,
    contextType: mockContextType,
    displayName: mockDisplayName,
    tenantId: null as string | null,
  };

  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      config = { ...config, ...parsed };
    } catch (e) {
      console.warn('[Mock LIFF] 無法解析 localStorage 設定:', e);
    }
  }

  // 若 env 有設定 VITE_MOCK_LINE_USER_ID，一律以 env 為準（避免 localStorage 舊值覆蓋導致 BOUND_TO_OTHER）
  if (import.meta.env.VITE_MOCK_LINE_USER_ID) {
    config.userId = import.meta.env.VITE_MOCK_LINE_USER_ID;
  }

  return config;
}

/**
 * 儲存 Mock 設定到 localStorage
 */
function saveMockConfig(config: ReturnType<typeof getMockConfig>) {
  const storageKey = 'mock_liff_config';
  localStorage.setItem(storageKey, JSON.stringify(config));
}

/**
 * 產生假的 LINE ID Token
 * 格式：mock_token_{userId}_{timestamp}
 */
function generateMockIdToken(userId: string): string {
  return `mock_token_${userId}_${Date.now()}`;
}

/**
 * Mock LIFF Hook
 * 在開發環境中提供模擬的 LIFF SDK 功能
 */
export function useMockLiff() {
  const [isReady, setIsReady] = useState(false);
  const [profile, setProfile] = useState<MockLiffProfile | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [config, setConfig] = useState(() => getMockConfig());

  // 監聽 URL 參數變化（當 URL 改變時重新讀取配置）
  useEffect(() => {
    const updateConfig = () => {
      const newConfig = getMockConfig();
      setConfig(newConfig);
    };

    // 初始讀取
    updateConfig();

    // 監聽 popstate 事件（瀏覽器前進/後退）
    window.addEventListener('popstate', updateConfig);

    // 監聽 pushstate/replacestate（透過程式碼改變 URL）
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      updateConfig();
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      updateConfig();
    };

    return () => {
      window.removeEventListener('popstate', updateConfig);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // 當 config 改變時，重新初始化狀態
  useEffect(() => {
    // 重置狀態
    setIsReady(false);
    setIsLoggedIn(false);
    setProfile(null);

    // 模擬初始化延遲
    const timer = setTimeout(() => {
      setIsReady(true);
      setIsLoggedIn(config.loggedIn);

      if (config.loggedIn) {
        setProfile({
          userId: config.userId,
          displayName: config.displayName,
          pictureUrl: `https://api.dicebear.com/7.x/avataaars/svg?seed=${config.userId}`,
        });
      } else {
        setProfile(null);
      }
    }, 100); // 模擬 100ms 的初始化時間

    return () => clearTimeout(timer);
  }, [config.loggedIn, config.userId, config.displayName, config.groupId]);

  // 模擬 liff.getIDToken()
  const getIDToken = useCallback(async (): Promise<string> => {
    if (!isLoggedIn) {
      throw new Error('User is not logged in');
    }
    return generateMockIdToken(config.userId);
  }, [isLoggedIn, config.userId]);

  // 模擬 liff.getProfile()
  const getProfile = useCallback(async (): Promise<MockLiffProfile> => {
    if (!isLoggedIn || !profile) {
      throw new Error('User is not logged in');
    }
    return profile;
  }, [isLoggedIn, profile]);

  // 模擬 liff.getContext()
  const getContext = useCallback(async (): Promise<MockLiffContext> => {
    if (config.contextType === 'group') {
      return {
        type: 'group',
        groupId: config.groupId,
        userId: config.userId,
      };
    } else if (config.contextType === 'room') {
      return {
        type: 'room',
        roomId: config.groupId, // 使用 groupId 作為 roomId
        userId: config.userId,
      };
    } else {
      return {
        type: config.contextType,
        userId: config.userId,
      };
    }
  }, [config.contextType, config.groupId, config.userId]);

  // 模擬 liff.openWindow()
  const openWindow = useCallback(
    (options: { url: string; external?: boolean }) => {
      console.log('[Mock LIFF] openWindow:', options);
      if (options.external) {
        // 在新視窗開啟
        window.open(options.url, '_blank');
      } else {
        // 在當前視窗開啟（不建議在真實 LIFF 中使用）
        window.location.href = options.url;
      }
    },
    []
  );

  // 模擬 liff.isLoggedIn()
  const checkIsLoggedIn = useCallback((): boolean => {
    return isLoggedIn;
  }, [isLoggedIn]);

  // 建立 Mock LIFF 物件
  const mockLiff: MockLiff = {
    isLoggedIn: checkIsLoggedIn,
    getIDToken,
    getProfile,
    getContext,
    openWindow,
  };

  return {
    liff: mockLiff,
    isReady,
    profile,
    isLoggedIn,
    error: null,
    // 提供設定更新功能（用於開發時動態調整）
    updateConfig: (newConfig: Partial<ReturnType<typeof getMockConfig>>) => {
      const updated = { ...config, ...newConfig };
      saveMockConfig(updated);
      // 重新載入頁面以套用新設定
      window.location.reload();
    },
  };
}

/**
 * 檢查是否應該使用 Mock LIFF
 */
export function shouldUseMockLiff(): boolean {
  // 優先檢查環境變數：如果明確設定了 VITE_ENABLE_LIFF_MOCK=true，就使用 Mock LIFF
  // 這允許在 dev/staging 環境中也能使用 Mock LIFF（例如 Vercel dev 環境）
  if (import.meta.env.VITE_ENABLE_LIFF_MOCK === 'true') {
    return true;
  }

  // 如果沒有明確設定，則只在開發環境中啟用（向後兼容）
  if (import.meta.env.MODE !== 'development' && import.meta.env.DEV !== true) {
    return false;
  }

  // 開發環境預設不使用 Mock LIFF（除非明確設定）
  return false;
}
